import { lazy } from 'react';
import type { AccountPanelComposition } from '../lib/accountPanels';
import { reportError } from './telemetry';

/**
 * Which feature's components fill which place on the account page (F1-d spec FR-S1, FR-S2, FR-S4;
 * WI-1.3 plan Decision 14). The composition lives here, in the composition root, and `router.tsx`
 * hands it to the account page's route as a prop, so `src/features/accounts` never imports another
 * feature (`features-do-not-import-each-other`) and no feature imports `src/app`
 * (`only-main-imports-app`). F11-f's `src/app/leadPanels.ts`, with a list per place.
 *
 * **How an owning feature adds its component**, in its own PR: build it in its own folder as a
 * component taking `AccountPanelProps` (or `AccountContactActionProps` for `contactActions`, from
 * `src/lib/accountPanels.ts`), test it there, and add it to its place's list here, **always as a
 * `lazy()` import** —
 *
 *     notes: [lazy(() => import('../features/notes/AccountNotesPanel'))],
 *
 * so each component stays in its own chunk and the account page's bundle budget does not grow with it
 * (`pnpm check:bundle`). The order within a list is the order on the page. An unfilled place renders
 * nothing.
 *
 * **`standards` is filled by F1-d** with F26-b's `AccountStandardsPanel` (FR-S4), for every user who
 * can open the page: its place is gated on no `buying_standard.*` key, and the panel decides its own
 * *Manage standards* link. The panel lives in `src/features/accounts`; it is still loaded lazily, so
 * its chunk is not part of the page's.
 *
 * **`sharing` is filled by F5-g** with `AccountSharingPanel` (F5-g FR-P1): the shared-with list and
 * *Share…*, for every user who can open the page; the panel hides its own controls from a user without
 * `account.manage` (F5-g FR-P4).
 *
 * **`actions` and `contactActions` are filled by F20-c** with the email feature's *Compose email* in the
 * header (F20-c FR-E1) and *Email* on each contact row (F20-c FR-E2). Each decides its own visibility —
 * `email.manage`, an active record, a contact with an address — and opens the compose dialog through
 * `useComposeEmail()` (`src/lib/email/composeEmail.ts`), so the email feature is never imported here
 * except by these loaders.
 *
 * **`actions` also holds F26-d's *Send this week's prices***, after *Compose email* (F26-d FR-U1): it shows for
 * `email.manage` on an active account, and hands the filled weekly-prices email to F20-c's opener.
 *
 * **`tags` is filled by F1-e** with `AccountTagsPanel` (F1-e FR-P1): the account's tags, and for an
 * `account.manage` holder *Add tag* and each tag's remove control.
 *
 * **`notes` is filled by F1-e** with `AccountNotesPanel` (F1-e FR-P2): the account's notes, newest
 * first, and for an `account.manage` holder the add box, *Edit* and *Delete*; it holds the page's one
 * leave guard for unsaved note text.
 *
 * **`contactActions` is also filled by F1-e** with `ContactNotesAndTagsAction` (F1-e FR-K1): *Notes
 * and tags* on each active contact row, after F20-c's *Email*, opening the contact's tags and notes in a
 * Modal, for every user who can open the page.
 *
 * **`timeline` is filled by F1-c** with `AccountTimelinePanel` (F1-c FR-P1): the account's
 * communication timeline, newest first, 20 entries at a time, for every user who can open the page.
 * The account page refreshes it from `onRecordChanged` through `accountTimelineKeys` in `src/lib`
 * (F1-c FR-P7), so no panel that writes a timeline row imports it.
 *
 * **`briefing` is filled by F13** with `AccountBriefingPanel` (F13 spec FR-V1): the *Before you meet this
 * customer* panel, first in the panel area, for a role holding `account.manage`; for any other role it renders
 * nothing, so the place's wrapper is empty and takes no space. Nothing is prepared until the person presses
 * *Prepare briefing*, and `onRecordChanged` prepares nothing (F13 spec decision 2).
 *
 * A component's render error stays inside its place and is reported to the telemetry seam, never to
 * the console.
 */

/** The standards panel's chunk, as a default export for `lazy()` — exported for its test. */
export const loadAccountStandardsPanel = () =>
    import('../features/accounts/AccountStandardsPanel').then((module) => ({
        default: module.AccountStandardsPanel,
    }));

const AccountStandardsPanelLazy = lazy(loadAccountStandardsPanel);

/** The sharing panel's chunk, as a default export for `lazy()` — exported for its test (F5-g FR-P1). */
export const loadAccountSharingPanel = () =>
    import('../features/sharing/AccountSharingPanel').then((module) => ({
        default: module.AccountSharingPanel,
    }));

const AccountSharingPanelLazy = lazy(loadAccountSharingPanel);

/** The header's *Compose email* chunk (F20-c FR-E1) — exported for its test. */
export const loadAccountComposeAction = () =>
    import('../features/email/recordEntries/AccountComposeAction').then((module) => ({
        default: module.AccountComposeAction,
    }));

/** The header's *Send this week's prices* chunk (F26-d FR-U1) — exported for its test. */
export const loadAccountPriceEmailAction = () =>
    import('../features/priceEmail/AccountPriceEmailAction').then((module) => ({
        default: module.AccountPriceEmailAction,
    }));

/** The contact row's *Email* chunk (F20-c FR-E2) — exported for its test. */
export const loadContactComposeAction = () =>
    import('../features/email/recordEntries/ContactComposeAction').then((module) => ({
        default: module.ContactComposeAction,
    }));

/** The tags panel's chunk, as a default export for `lazy()` — exported for its test (F1-e FR-P1). */
export const loadAccountTagsPanel = () =>
    import('../features/notesAndTags/AccountTagsPanel').then((module) => ({
        default: module.AccountTagsPanel,
    }));

const AccountTagsPanelLazy = lazy(loadAccountTagsPanel);

/** The notes panel's chunk, as a default export for `lazy()` — exported for its test (F1-e FR-P2). */
export const loadAccountNotesPanel = () =>
    import('../features/notesAndTags/AccountNotesPanel').then((module) => ({
        default: module.AccountNotesPanel,
    }));

const AccountNotesPanelLazy = lazy(loadAccountNotesPanel);

/** The contact action's chunk, as a default export for `lazy()` — exported for its test (F1-e FR-K1). */
export const loadContactNotesAndTagsAction = () =>
    import('../features/notesAndTags/ContactNotesAndTagsAction').then((module) => ({
        default: module.ContactNotesAndTagsAction,
    }));

const ContactNotesAndTagsActionLazy = lazy(loadContactNotesAndTagsAction);

/** The timeline panel's chunk, as a default export for `lazy()` — exported for its test (F1-c FR-P1). */
export const loadAccountTimelinePanel = () =>
    import('../features/timeline/AccountTimelinePanel').then((module) => ({
        default: module.AccountTimelinePanel,
    }));

/** The briefing panel's chunk, as a default export for `lazy()` — exported for its test (F13 spec FR-V1). */
export const loadAccountBriefingPanel = () =>
    import('../features/accountBriefing/AccountBriefingPanel').then((module) => ({
        default: module.AccountBriefingPanel,
    }));

export const accountPanelComposition: AccountPanelComposition = {
    panels: {
        briefing: [lazy(loadAccountBriefingPanel)],
        tags: [AccountTagsPanelLazy],
        sharing: [AccountSharingPanelLazy],
        standards: [AccountStandardsPanelLazy],
        notes: [AccountNotesPanelLazy],
        timeline: [lazy(loadAccountTimelinePanel)],
        actions: [lazy(loadAccountComposeAction), lazy(loadAccountPriceEmailAction)],
        contactActions: [lazy(loadContactComposeAction), ContactNotesAndTagsActionLazy],
    },
    reportPanelError: (error, place) => reportError({ error, where: `account panel ${place}` }),
};
